/* eslint-disable react-hooks/exhaustive-deps */
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { pushCartUpdateToDataLayer } from 'src/components/pages/cart/utils/pushCartUpdateToDataLayer';
import { COUPON_DISCOUNT_DETAIL_BUTTON } from 'src/constants/coupon';
import { useCart } from 'src/hooks/useCart';
import { useCartQuantity } from 'src/hooks/useCartQuantity';
import { useModal } from 'src/hooks/useModal';
import { useShopSelector } from 'src/hooks/useShopSelector';
import { orderLimits } from 'src/libs/api/orderLimits';
import type { CartProductsSchema } from 'src/models/cart/CartProductsSchema';
import type { CartItem } from 'src/models/orderLimits/schema';
import type { Product } from 'src/models/product/schema';
import { AuthContext } from 'src/stores/Auth';
import { CategoryContext } from 'src/stores/Category';
import { FeatureTagsContext } from 'src/stores/FeatureTags';
import { I18nContext } from 'src/stores/I18n';
import type { LOCALE_TYPE } from 'src/types/locale';
import { combineCart } from 'src/utils/cart';
import { getCartFromCookie, saveCartToCookie } from 'src/utils/cookies';
import { initAndPushCloudRetailDataLayer } from 'src/utils/createDataLayerObject';
import { getDiscountRateByLocale } from 'src/utils/medicine';
import { mixpanel } from 'src/utils/mixpanel';
import {
  isAlcoholProduct,
  isDrugProduct,
  isLimitedSaleProduct,
  mapProductsForDataLayer,
  taxProductsByPath
} from 'src/utils/productHelper';

type CartDetails = {
  product: Product;
  quantity: number;
};

type Params = { targetAttributionToken?: string };

export const useCartAdd = (params?: Params) => {
  const targetAttributionToken = params?.targetAttributionToken;
  const { setIsVisible, setModalParams } = useModal('noticeModal');
  const {
    setIsVisible: setIsShopModalVisible,
    isVisible: isShopModalVisible,
    setIgnitionSource: setShopSelectModalIgnitionSource
  } = useModal('shopSelectModal');
  const {
    setIsVisible: setIsChemicalModal,
    setModalParams: setChemicalModalParams,
    modalParams: chemicalModalParams
  } = useModal('chemicalConfirmModal');
  const {
    setIsVisible: setIsAgeVerificationModal,
    setModalParams: setAgeVerificationModalParams,
    modalParams: ageVerificationModalParams
  } = useModal('alcoholAgeVerificationModal');
  const {
    setIsVisible: setIsLimitedSaleProductModal,
    setModalParams: setLimitedSaleProductModalParams,
    modalParams: limitedSaleProductModalParams
  } = useModal('limitedSaleProductModal');
  const {
    setIsVisible: setIsDiscountNoticeModal,
    setModalParams: setDiscountNoticeModalParams,
    modalParams: discountNoticeModalParams
  } = useModal('discountNoticeModal');
  const localeResources = useContext(I18nContext);
  const { pathname, locale } = useRouter();
  const { currentShop } = useShopSelector();
  const category = useContext(CategoryContext);
  const { user } = useContext(AuthContext);
  const featureTags = useContext(FeatureTagsContext);
  const currentShopId = currentShop?.id || NaN;
  const [cartDetails, setCartDetails] = useState<CartDetails | null>(null);
  const { updateCart, cart } = useCart();
  const { mutate: mutateCartQuantity } = useCartQuantity();
  const [shopModalState, setShopModalState] = useState<'shopSelectShown' | 'shopSelectCompleted' | 'initState'>(
    'initState'
  );

  useEffect(() => {
    if (currentShop && shopModalState === 'shopSelectShown') {
      //globalのshop状態存在する時かつshopSelectShown
      setShopModalState('shopSelectCompleted');
    }
  }, [isShopModalVisible]);

  useEffect(() => {
    if (shopModalState === 'shopSelectCompleted' && cartDetails) {
      setTimeout(() => {
        onAddToCart(cartDetails.product, cartDetails.quantity, 'shop_select_completed');
      }, 2000);
    }
  }, [shopModalState, cartDetails]);

  const mergedCartItems = (current: CartItem[]) => {
    if (!cart) return current;
    const prevCartItems: CartItem[] = cart.flatMap((item) => {
      return {
        productId: item.productId,
        quantity: item.quantity
      };
    });
    return combineCart(prevCartItems, current);
  };

  const onAddToCart = async (product: Product, quantity: number, section: string) => {
    if (!currentShopId) {
      mixpanel.onShopModalOpen('cartAdd');
      setShopModalState('shopSelectShown');
      setShopSelectModalIgnitionSource('add_cart');
      setIsShopModalVisible(true);
      setCartDetails({ product, quantity });
      return;
    }

    if (
      section === COUPON_DISCOUNT_DETAIL_BUTTON &&
      !product.taxExempted &&
      !discountNoticeModalParams.product &&
      !discountNoticeModalParams.quantity
    ) {
      setDiscountNoticeModalParams({
        type: 'info',
        product: product,
        quantity: quantity,
        extraData: targetAttributionToken ? { targetAttributionToken } : undefined
      });
      setIsDiscountNoticeModal(true);
      return;
    }

    if (!product.orderable) {
      setModalParams({
        type: 'warning',
        text: localeResources.cartError.productNotOrderable
      });
      setIsVisible(true);
      return;
    }

    if (
      isLimitedSaleProduct(product, featureTags) &&
      product.shippingDeadline &&
      !limitedSaleProductModalParams.product &&
      !limitedSaleProductModalParams.quantity
    ) {
      setLimitedSaleProductModalParams({
        type: 'info',
        product: product,
        quantity: quantity,
        extraData: targetAttributionToken ? { targetAttributionToken } : undefined
      });
      setIsLimitedSaleProductModal(true);
      return;
    }

    if (
      taxProductsByPath(pathname, product) &&
      !product.taxExempted &&
      !chemicalModalParams.product &&
      !chemicalModalParams.quantity
    ) {
      setChemicalModalParams({
        type: 'info',
        product: product,
        quantity: quantity,
        extraData: targetAttributionToken ? { targetAttributionToken } : undefined
      });
      setIsChemicalModal(true);
      return;
    }

    if (
      isAlcoholProduct(product, category) &&
      !ageVerificationModalParams.product &&
      !ageVerificationModalParams.quantity
    ) {
      setAgeVerificationModalParams({
        type: 'info',
        product: product,
        quantity: quantity,
        extraData: targetAttributionToken ? { targetAttributionToken } : undefined
      });
      setIsAgeVerificationModal(true);
      return;
    }

    setShopModalState('initState');
    setCartDetails(null);
    const orderLimitsResponse = await orderLimits({
      shopId: currentShopId,
      lineItems: [
        {
          productId: product.productId,
          quantity: quantity
        }
      ],
      pickup_date: ''
    });
    if (orderLimitsResponse.isLeft()) {
      setModalParams({
        type: 'warning',
        title: localeResources.commons.errorOccurred,
        text: orderLimitsResponse.value.errors[0]
      });
      setIsVisible(true);
      mixpanel.onProductCannotAddToCart(product.productId, quantity, orderLimitsResponse.value.errors[0]);
      return;
    }
    mixpanel.onCartAdd(product.productId, quantity, section);
    const newCart = [{ productId: product.productId, quantity }];
    const newCartItems = user ? mergedCartItems(newCart) : combineCart(getCartFromCookie(), newCart);
    if (!user) {
      saveCartToCookie(newCartItems);
    }
    await updateCart(currentShopId, newCartItems, section);
    mutateCartQuantity();
    pushCartUpdateToDataLayer(cart, product, quantity, locale as LOCALE_TYPE, true);
    setModalParams({
      type: 'info',
      title: isDrugProduct(product, category)
        ? localeResources.discountNotice.addToCart(getDiscountRateByLocale(locale))
        : localeResources.cart.addedToCart
    });
    initAndPushCloudRetailDataLayer({
      eventType: 'add-to-cart',
      productDetails: mapProductsForDataLayer([product], quantity),
      attributionToken: targetAttributionToken
    });
    setIsVisible(true);
  };

  const onBulkAddToCart = async (productList: CartProductsSchema[], section?: string) => {
    if (!currentShopId) {
      mixpanel.onShopModalOpen('cartAdd');
      setShopModalState('shopSelectShown');
      setShopSelectModalIgnitionSource('add_cart');
      setIsShopModalVisible(true);
      productList.map((product) => {
        setCartDetails({ product, quantity: product.quantity });
      });
      return;
    }

    // checkが全て外れていた場合は何もしない
    if (productList.length === 0) return;
    productList.map((product) => {
      if (!product.orderable) {
        setModalParams({
          type: 'warning',
          text: localeResources.cartError.productNotOrderable
        });
        setIsVisible(true);
        return;
      }
    });
    const orderLimitsResponse = await orderLimits({
      shopId: currentShopId,
      lineItems: productList.flatMap((product) => {
        return { productId: product.productId, quantity: product.quantity };
      }),
      pickup_date: ''
    });
    if (orderLimitsResponse.isLeft()) {
      setModalParams({
        type: 'warning',
        title: localeResources.commons.errorOccurred,
        text: orderLimitsResponse.value.errors[0]
      });
      setIsVisible(true);
      productList.map((product) => {
        mixpanel.onProductCannotAddToCart(product.productId, product.quantity, orderLimitsResponse.value.errors[0]);
      });
      return;
    }
    const addingItems: CartItem[] = productList.flatMap((item) => {
      return {
        productId: item.productId,
        quantity: item.quantity
      };
    });
    const newCartItems = user ? mergedCartItems(addingItems) : combineCart(getCartFromCookie(), addingItems);
    if (!user) {
      saveCartToCookie(newCartItems);
    }
    await updateCart(currentShopId, newCartItems, section ?? 'bulk_add_to_cart');
    mutateCartQuantity();
    setModalParams({
      type: 'info',
      title: localeResources.cart.addedToCart
    });
    initAndPushCloudRetailDataLayer({
      eventType: 'add-to-cart',
      productDetails: mapProductsForDataLayer(productList, 1),
      attributionToken: targetAttributionToken
    });
    setIsVisible(true);
  };

  return {
    onAddToCart,
    onBulkAddToCart
  };
};
